// Combobox de busca de contato — único no CRM.
//
// Antes existiam dois quase-duplicados (NewDealSheet e CrmDealDetail) que
// divergiram: o de participantes não tinha estado de "buscando" e mostrava
// menos dados na linha. Unificado aqui; as diferenças reais viraram props.
import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import QuickCreateContactDialog from "@/components/Crm/QuickCreateContactDialog";
import { useDebounce } from "@/hooks/useDebounce";
import { MIN_SEARCH_LENGTH } from "@shared/const";
import { Check, ChevronsUpDown, Loader2, UserPlus } from "lucide-react";
import {
  ContactResultRow,
  contactFullName,
  contactInitials,
  type ContactLite,
} from "@/components/Crm/contact-display";

export type ContactSearchInherit = {
  companyName?: string | null;
  sector?: string | null;
  city?: string | null;
  state?: string | null;
  contactType?: "public_admin" | "empresa" | "cliente_final" | null;
};

export default function ContactSearchCombobox({
  value = null,
  onChange,
  defaultOpen = false,
  limit = 25,
  inherit,
  filters,
}: {
  /** Contato já escolhido — quando presente, aparece no gatilho com um check. */
  value?: ContactLite | null;
  onChange: (c: ContactLite) => void;
  /** O sheet de nova oportunidade abre o picker já aberto. */
  defaultOpen?: boolean;
  limit?: number;
  /** Pré-popula o QuickCreate (herdar órgão/cidade do contato principal). */
  inherit?: ContactSearchInherit;
  /** Filtros explícitos combináveis com a busca (o servidor já os aceita). */
  filters?: { state?: string; city?: string; organ?: string };
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  // Sem debounce, cada tecla disparava uma listagem de contatos inteira.
  // 300ms é o mesmo intervalo da busca da aba Contatos.
  const debouncedQuery = useDebounce(query, 300);
  const trimmed = debouncedQuery.trim();
  // MIN_SEARCH_LENGTH espelha o piso do servidor: abaixo dele o termo é
  // descartado lá e a lista voltaria inteira, sem indicação de por quê.
  const search = trpc.contacts.list.useQuery(
    {
      search: trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined,
      limit,
      // O picker não usa `total` — pular o count(*) corta ~metade da latência.
      skipTotal: true,
      ...(filters ?? {}),
    } as any,
    { enabled: open, placeholderData: (prev: any) => prev },
  );

  const list: ContactLite[] = useMemo(() => {
    const raw = search.data as any;
    return Array.isArray(raw) ? raw : raw?.contacts ?? [];
  }, [search.data]);

  const trimmedQuery = query.trim();

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted/40 transition"
          >
            {value ? (
              <span className="flex items-center gap-2 min-w-0">
                <Avatar className="h-6 w-6 shrink-0">
                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                    {contactInitials(contactFullName(value))}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {contactFullName(value)}
                  {value.companyName && (
                    <span className="text-muted-foreground"> · {value.companyName}</span>
                  )}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">Buscar contato…</span>
            )}
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        {/* min-w: a linha agora tem 3 níveis (nome / órgão / cidade · cargo) e
            na largura do gatilho o órgão truncava cedo demais. */}
        <PopoverContent
          className="w-[--radix-popover-trigger-width] min-w-[360px] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                placeholder={`Nome, órgão, cidade ou telefone (${MIN_SEARCH_LENGTH}+ letras)...`}
                value={query}
                onValueChange={setQuery}
              />
              {/* Refetch com lista já na tela: spinner discreto no campo, sem
                  esconder os resultados anteriores. */}
              {search.isFetching && !search.isLoading && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground absolute right-3 top-3.5" />
              )}
            </div>
            <CommandList className="max-h-[340px]">
              {/* Só o PRIMEIRO carregamento troca a lista pelo spinner. Usar
                  isFetching aqui anulava o placeholderData logo acima: a cada
                  refetch a lista sumia e voltava, piscando a cada tecla. Nos
                  refetches seguintes os resultados anteriores ficam na tela e
                  o spinner some para o cabeçalho. */}
              {search.isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                  Buscando…
                </div>
              ) : list.length === 0 ? (
                <div className="py-6 px-3 text-center">
                  <div className="mx-auto h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-2">
                    <UserPlus className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium mb-1">Nenhum contato encontrado</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {trimmedQuery
                      ? `Nenhum resultado para "${trimmedQuery}".`
                      : "Digite para buscar ou crie um novo."}
                  </p>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setOpen(false);
                      setQuickCreateOpen(true);
                    }}
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    {trimmedQuery ? `Criar "${trimmedQuery}"` : "Criar novo contato"}
                  </Button>
                </div>
              ) : (
                <>
                  <CommandGroup>
                    {list.slice(0, 50).map((c) => (
                      <CommandItem
                        key={c.id}
                        onSelect={() => {
                          onChange(c);
                          setOpen(false);
                        }}
                        className="flex items-center gap-2"
                      >
                        <ContactResultRow c={c} />
                        {value?.id === c.id && (
                          <Check className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setOpen(false);
                        setQuickCreateOpen(true);
                      }}
                      className="text-primary"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {trimmedQuery
                        ? `Criar novo: "${trimmedQuery}"`
                        : "Criar novo contato"}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <QuickCreateContactDialog
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        initialName={trimmedQuery}
        initialCompanyName={inherit?.companyName}
        initialSector={inherit?.sector}
        initialCity={inherit?.city}
        initialState={inherit?.state}
        initialContactType={inherit?.contactType ?? undefined}
        onCreated={(c) => {
          onChange(c as ContactLite);
          setQuery("");
        }}
      />
    </>
  );
}
